import { format } from "date-fns";
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { GridSortDirection } from "@mui/x-data-grid-pro";
import { TransactionsReportType } from "../../../api/transactions";
import { dateRange, DateRangeConfig } from "../../../helpers/dateUtils";
import useBulkExport from "../../../helpers/useBulkExport";
import { TransactionReportFilters } from "../../../models/transactions";
import { ExportDialog } from "./ExportDialog";

export interface ExportDialogWithApiProps {
  filters: TransactionReportFilters;
  reportType: TransactionsReportType;
  start: boolean;
  onComplete: () => void;
  sortBy: string;
  sortOrder: GridSortDirection;
  dateRangeConfig: DateRangeConfig;

  isAdmin?: boolean;
  search?: string;
}

export const ExportDialogWithApi = ({
  filters,
  reportType,
  start,
  onComplete,
  sortBy,
  sortOrder,
  dateRangeConfig,
  isAdmin,
  search
}: ExportDialogWithApiProps) => {

  const [open, setOpen] = useState(false);
  const [filename, setFilename] = useState<string>();

  const generateFilename = useCallback(() => {
    const ts = format(new Date(), "yyyyMMdd_kkmmss");
    return `Transaction_Report_${ts}.csv`;
  }, []);

  useEffect(() => {
    if (!start) return;

    setFilename(generateFilename());
    setOpen(true);
  }, [start, generateFilename]);

  const onClose = () => {
    setOpen(false);
    setFilename(undefined);
    onComplete?.();
  };

  const { startDate, endDate } = dateRange(
    filters.period,
    filters.periodFrom,
    filters.periodTo,
    undefined,
    dateRangeConfig
  );

  const exportParams = useMemo(() => ({
    startDate,
    endDate,
    sortBy,
    sortOrder: sortOrder as string,
    filename,
    reportType,
    isAdmin,
    location: filters.location?.id,
    locationArea: filters.locationArea?.id,
    staff: filters.staff?.id,
    device: filters.device?.id,
    paymentMethod: filters.paymentMethod?.id,
    search: search || undefined,
    companyGuid: filters.companyGuid,
    hasActiveFlag: filters.hasActiveFlag,
    activeFlagCategory: filters.activeFlagCategory
  }), [
    startDate,
    endDate,
    sortBy,
    sortOrder,
    filename,
    reportType,
    isAdmin,
    filters.location?.id,
    filters.locationArea?.id,
    filters.staff?.id,
    filters.device?.id,
    filters.paymentMethod?.id,
    search,
    filters.companyGuid,
    filters.hasActiveFlag,
    filters.activeFlagCategory
  ]);

  const { progress, fileUri, error } = useBulkExport(exportParams);

  return (
    <ExportDialog
      open={open}
      progress={progress}
      fileUri={fileUri}
      filename={filename}
      error={error}
      onCancel={() => {/* no op */}}
      onClose={onClose}
      onRetry={() => setFilename(generateFilename())}
    />
  );
};
